
import {
  defineComponent,
  ref,
  computed,
  PropType,
  onMounted,
  watch,
} from "vue";
import { usePlugin } from "@/plugins";
import {
  TrainingDocument,
  Course,
  UpsertTrainingDocumentRequest,
  TrainingTypeEnum,
  TrainingDocumentDetailDto,
  CourseDetailDto,
  GetTrainingDocumentRequest,
  GetTrainingDocumentResponse,
  ConfigQuestionSet,
  GetConfigQuestionSetResponse,
  UpsertTrainingDocumentResponse,
} from "@/types";
import Fuse from "fuse.js";
import { clone } from "ramda";
import InputInstructor from "@/components/InputInstructor.vue";
import BackButton from "./BackButton.vue";
import InputCourse from "@/components/InputCourse.vue";
import InputUser from "@/components/InputUser.vue";
import InputQuestion from "@/components/InputQuestion.vue";
import StatusBadge from "@/components/StatusBadge.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default defineComponent({
  name: "TrainingDocumentList",
  props: {
    items: Array as PropType<TrainingDocumentDetailDto[]>,
    course: {} as PropType<Course>,
  },
  components: {
    InputCourse,
    InputInstructor,
    InputQuestion,
    InputUser,
    BackButton,
    StatusBadge,
  },
  emit: ["newTrainingDocument"],
  setup(props) {
    const {
      success,
      error,
      axios,
      setLoading,
      onRouterPush,
      trainingTypeDesc,
      confirmation,
      formatAppDate,
      dayjs,
    } = usePlugin();

    const isFullScreen = computed(() => props.course === undefined);
    const filteredDates = ref<Date[]>([
      dayjs()
        .startOf("month")
        .toDate(),
      dayjs()
        .endOf("month")
        .toDate(),
    ]);
    const trainingDocuments = ref<TrainingDocumentDetailDto[]>([]);
    const trainingDocument = ref<TrainingDocument>({} as TrainingDocument);
    const selectedDate = ref<Date[]>([dayjs().toDate(), dayjs().toDate()]);
    watch(
      () => selectedDate.value,
      () => {
        trainingDocument.value.startDate = selectedDate.value[0];
        trainingDocument.value.endDate = selectedDate.value[1];
      }
    );

    const showDialog = ref(false);
    // const popupDeleteDialog = ref(false);
    const searchString = ref("");
    const submitted = ref(false);
    const disableEdit = ref(false);

    const newRules = {
      trainingDocument: {
        trainingType: { required },
        courseId: { required },
        location: { required },
        startDate: { required },
        endDate: { required },
        time: { required },
        hours: { required },
        dunning1: { required },
        dunning2: { required },
      },
    };
    const v$ = useVuelidate(newRules, { trainingDocument });

    const filteredTrainingDocument = computed(() => {
      if (!searchString.value) return trainingDocuments.value;
      const fuse = new Fuse(trainingDocuments.value, {
        keys: [
          "trainingDocument.id",
          "trainingDocument.courseId",
          "trainingDocument.course.description",
        ],
      });
      const searchResult = fuse.search(searchString.value);
      return searchResult.map((x) => x.item);
    });

    const onSaveTrainingDocument = async () => {
      v$.value.$touch();
      if (v$.value.$invalid) return;

      submitted.value = true;
      setLoading(true);
      const trainingDocumentUpdate = {
        id: "",
        courseId: trainingDocument.value.courseId,
        startDate: selectedDate.value[0],
        endDate: selectedDate.value[1],
        location: trainingDocument.value.location,
        course: trainingDocument.value.course,
        trainingType: trainingDocument.value.trainingType,
        instructorQuestionId: trainingDocument.value.instructorQuestionId,
        satisfactionQuestionId: trainingDocument.value.satisfactionQuestionId,
        knowledgeQuestionId: trainingDocument.value.knowledgeQuestionId,
        instructorWaitingPeriod: trainingDocument.value.instructorWaitingPeriod,
        satisfactionWaitingPeriod:
          trainingDocument.value.satisfactionWaitingPeriod,
        knowledgeWaitingPeriod: trainingDocument.value.knowledgeWaitingPeriod,
        instructorId1: trainingDocument.value.instructorId1,
        instructorId2: trainingDocument.value.instructorId2,
        instructorId3: trainingDocument.value.instructorId3,
        internalInstructorId1: trainingDocument.value.internalInstructorId1,
        internalInstructorId2: trainingDocument.value.internalInstructorId2,
        internalInstructorId3: trainingDocument.value.internalInstructorId3,
        dunning1: trainingDocument.value.dunning1,
        dunning2: trainingDocument.value.dunning2,
        hours: trainingDocument.value.hours,
        time: trainingDocument.value.time,
        remark: trainingDocument.value.remark,
      } as TrainingDocument;

      try {
        const result = await axios.post<UpsertTrainingDocumentResponse>(
          "api/v1/services/trainingDocument/upsert",
          {
            trainingDocuments: [trainingDocumentUpdate],
          } as UpsertTrainingDocumentRequest
        );
        success("บันทึกข้อมูลสำเร็จแล้ว");
        showDialog.value = false;
        if (result.data.trainingDocument?.id) {
          onRouterPush({
            name: "editTrainingDocument",
            params: { docNo: result.data.trainingDocument.id },
          });
        } else {
          await loadTrainingDocuments();
        }
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onHideDialog = () => {
      showDialog.value = false;
      submitted.value = false;
    };

    const onNewTrainingDocument = async () => {
      v$.value.$reset();
      trainingDocument.value = {
        course: props.course,
        courseId: props.course?.id,
        trainingType: TrainingTypeEnum.Internal,
        startDate: selectedDate.value[0],
        endDate: selectedDate.value[1],
      } as TrainingDocument;
      trainingDocument.value.instructorQuestionId =
        questionSet.value?.instructorQuestionId ?? null;
      trainingDocument.value.satisfactionQuestionId =
        questionSet.value?.satisfactionQuestionId ?? null;
      trainingDocument.value.knowledgeQuestionId =
        questionSet.value?.knowledgeQuestionId ?? null;
      trainingDocument.value.instructorWaitingPeriod =
        questionSet.value?.instructorWaitingPeriod ?? null;
      trainingDocument.value.satisfactionWaitingPeriod =
        questionSet.value?.satisfactionWaitingPeriod ?? null;
      trainingDocument.value.knowledgeWaitingPeriod =
        questionSet.value?.knowledgeWaitingPeriod ?? null;
      trainingDocument.value.dunning1 = questionSet.value?.dunning1 ?? null;
      trainingDocument.value.dunning2 = questionSet.value?.dunning2 ?? null;
      showDialog.value = true;
      submitted.value = false;
    };

    const questionSet = computed(() => {
      var question = questionSets.value.filter(
        (x) => x.trainingType === trainingDocument.value.trainingType
      );
      return question.length > 0 ? question[0] : null;
    });

    const onEditTrainingDocument = async (
      source: TrainingDocumentDetailDto
    ) => {
      onRouterPush({
        name: "editTrainingDocument",
        params: {
          // id: props.course?.id ?? "",
          docNo: source.trainingDocument?.id ?? "",
        },
      });
    };

    const onDeleteTrainingDocument = async (
      source: TrainingDocumentDetailDto
    ) => {
      trainingDocument.value = clone(
        source.trainingDocument ?? ({} as TrainingDocument)
      );

      confirmation.require({
        header: "ลบเอกสาร",
        message: `กรุณายืนยันเพื่อลบเอกสาร ${trainingDocument.value.id} ?`,
        acceptLabel: "ลบเอกสาร",
        accept: () => handleDeleteTrainingDocument(),
        rejectLabel: "ปิด",
      });
    };

    const loadTrainingDocuments = async () => {
      setLoading(true);
      try {
        const result = await axios.get<CourseDetailDto>(
          `api/v1/services/course/${props.course?.id}`
        );
        trainingDocuments.value = result.data.trainingDocumentDtos ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    onMounted(async () => {
      trainingDocuments.value = clone(props.items) ?? [];
      if (isFullScreen.value === true) {
        onSearch();
      }
    });

    const handleDeleteTrainingDocument = async () => {
      setLoading(true);
      try {
        await axios.delete("api/v1/services/trainingDocument/delete", {
          data: {
            trainingDocuments: [trainingDocument.value],
          } as UpsertTrainingDocumentRequest,
        });
        await loadTrainingDocuments();
        if (isFullScreen.value === true) {
          onSearch();
        }
        success("ลบข้อมูลสำเร็จแล้ว");
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
      // }
    };

    const onSearch = async () => {
      setLoading(true);
      try {
        var result = await axios.post<GetTrainingDocumentResponse>(
          "api/v1/services/trainingDocument/get",
          {
            fromDate: dayjs(filteredDates.value[0]).toISOString(),
            toDate: dayjs(filteredDates.value[1]).toISOString(),
            searchString: searchString.value,
          } as GetTrainingDocumentRequest
        );
        trainingDocuments.value = (result.data.trainingDocuments ?? []).map(
          (x) => {
            return {
              trainingDocument: x,
            } as TrainingDocumentDetailDto;
          }
        );
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const questionSets = ref<ConfigQuestionSet[]>([]);
    const loadQuestionSet = async () => {
      setLoading(true);
      try {
        const result = await axios.post<GetConfigQuestionSetResponse>(
          "api/v1/services/configQuestionSet",
          {}
        );
        questionSets.value = result.data.configQuestionSets ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onInputTrainingType = () => {
      trainingDocument.value.instructorQuestionId =
        questionSet.value?.instructorQuestionId ?? null;
      trainingDocument.value.satisfactionQuestionId =
        questionSet.value?.satisfactionQuestionId ?? null;
      trainingDocument.value.knowledgeQuestionId =
        questionSet.value?.knowledgeQuestionId ?? null;
      trainingDocument.value.instructorWaitingPeriod =
        questionSet.value?.instructorWaitingPeriod ?? null;
      trainingDocument.value.satisfactionWaitingPeriod =
        questionSet.value?.satisfactionWaitingPeriod ?? null;
      trainingDocument.value.knowledgeWaitingPeriod =
        questionSet.value?.knowledgeWaitingPeriod ?? null;
    };

    onMounted(async () => {
      await loadQuestionSet();
    });

    return {
      onNewTrainingDocument,
      onEditTrainingDocument,
      onDeleteTrainingDocument,
      searchString,
      filteredTrainingDocument,
      trainingDocument,
      submitted,
      onHideDialog,
      showDialog,
      onSaveTrainingDocument,

      handleDeleteTrainingDocument,
      TrainingTypeEnum,
      formatAppDate,
      trainingTypeDesc,
      disableEdit,
      isFullScreen,
      filteredDates,
      onSearch,
      selectedDate,
      v$,
      onInputTrainingType,
    };
  },
});
